import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Terms of Service | Close Now Lending"
        description="Terms of Service for Close Now Lending. Review the terms and conditions governing your use of our website and real estate lending services."
        url="/terms"
      />
      <Header />

      {/* Hero */}
      <section className="bg-slate-900 pt-28 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">Terms of Service</h1>
            <p className="text-gray-400 text-sm">Last updated: February 1, 2026</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto prose prose-gray prose-lg" data-testid="terms-content">

            <p className="text-gray-700 leading-relaxed">
              Welcome to Close Now Lending. These Terms of Service ("Terms") govern your access to and use of our website and services. By accessing or using our website, you agree to be bound by these Terms. If you do not agree, please do not use our website.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">1. Services Overview</h2>
            <p className="text-gray-700 leading-relaxed">
              Close Now Lending provides real estate investment lending services, including but not limited to hard money loans, fix and flip financing, DSCR loans, cash out refinance, new construction loans, bridge loans, and commercial/multi-family lending. Our website provides information about our services, loan calculators, educational resources, and the ability to submit loan inquiries.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">2. Eligibility</h2>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 18 years old and legally capable of entering into binding contracts to use our services. By submitting a loan application or inquiry, you represent that all information provided is accurate and complete.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">3. Not a Commitment to Lend</h2>
            <p className="text-gray-700 leading-relaxed">
              Information on this website, including loan programs, rates, and terms, is provided for informational purposes only and does not constitute a commitment to lend, a loan approval, or a guaranteed rate. All loan applications are subject to underwriting review, credit approval, property appraisal, and other conditions. Loan terms, rates, and availability may vary based on market conditions, borrower qualifications, and property characteristics.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">4. Use of Website</h2>
            <p className="text-gray-700 leading-relaxed">You agree to use our website only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li>Use the website in any way that violates applicable federal, state, or local laws</li>
              <li>Submit false, misleading, or inaccurate information</li>
              <li>Attempt to gain unauthorized access to our systems or data</li>
              <li>Use automated tools (bots, scrapers) to access or collect data from the website</li>
              <li>Interfere with or disrupt the operation of the website</li>
              <li>Reproduce, distribute, or create derivative works based on website content without permission</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">5. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              All content on this website, including text, graphics, logos, images, and software, is the property of Close Now Lending or its licensors and is protected by United States and international copyright, trademark, and other intellectual property laws. You may not reproduce, modify, distribute, or publicly display any content without our prior written consent.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">6. Calculator Tools and Estimates</h2>
            <p className="text-gray-700 leading-relaxed">
              The loan calculators and estimation tools on our website (including the DSCR Calculator and Fix & Flip Deal Calculator) are provided for educational and informational purposes only. Results are estimates and should not be relied upon as financial advice or a guarantee of loan terms. Actual loan terms may differ based on your specific circumstances and underwriting.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">7. Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Your use of our website is also governed by our <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>, which describes how we collect, use, and protect your personal information. By using our website, you consent to the practices described in our Privacy Policy.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">8. Disclaimer of Warranties</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website and services are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied. We disclaim all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the website will be uninterrupted, error-free, or free of viruses or other harmful components.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the fullest extent permitted by law, Close Now Lending, its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the website or services, including but not limited to loss of profits, data, or goodwill, even if we have been advised of the possibility of such damages.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">10. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold harmless Close Now Lending and its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses (including reasonable attorneys' fees) arising from your use of the website, violation of these Terms, or infringement of any third-party rights.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">11. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to conflict of law principles. Any disputes arising under these Terms shall be resolved exclusively in the state or federal courts located in Harris County, Texas.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">12. Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the website. Your continued use of the website after changes are posted constitutes acceptance of the revised Terms.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mt-10 mb-4">13. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-slate-50 rounded-lg p-6 mt-4 not-prose">
              <p className="font-semibold text-gray-900">Close Now Lending</p>
              <p className="text-gray-600 mt-1">4601 Yale St, Houston, TX 77018</p>
              <p className="text-gray-600">Phone: <a href="tel:+19034762937" className="text-blue-600 hover:underline">903-476-2937</a></p>
              <p className="text-gray-600">Email: <a href="mailto:info@closenowlending.com" className="text-blue-600 hover:underline">info@closenowlending.com</a></p>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
